import React, { useState, useEffect, useRef } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";

import { customStyles } from "../../../Bookclosure/Components/AddTaskModal";
import commoncss from "./../../css/common.module.css";
import buttoncss from "./../../css/button.module.css";
import libcss from "./../../css/library.module.css";
import sidePanelStyles from "../../css/cashFlowTable.module.css";
import Modal from "./../../elements/Modal";
import { Button } from "./../../elements/Button";
import Dropdown from "./../../elements/Dropdown";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/formulaPlus.svg";
import { ReactComponent as AddPlusIcon } from "../../assets/icons/grayPlus.svg";
import { ReactComponent as MinusIcon } from "../../assets/icons/MinusIcon.svg";
import { ReactComponent as MultiplyIcon } from "../../assets/icons/MultiplyIcon.svg";
import { ReactComponent as DivisionIcon } from "../../assets/icons/DivisionIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/AddElement.svg";
import InputGroup from "./../../elements/InputGroup";
import CustomDropdownIndicator from "../../elements/CustomDropdownIndicator";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import adminPortalCss from "../../css/adminportal.module.css";
import { TbPlus, TbMinus, TbX, TbDivide } from "react-icons/tb";

const animatedComponents = makeAnimated();

const OPERATORS = ["positive", "negative", "multiply", "divide"];

const OperatorIcon = ({ type, size = 18 }) => {
  switch(type) {
    case "positive": return <TbPlus size={size} />;
    case "negative": return <TbMinus size={size} />;
    case "multiply": return <TbX size={size} />;
    case "divide": return <TbDivide size={size} />;
    default: return <TbPlus size={size} />;
  }
};

const OperatorBtn = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      {open && (
        <div 
          className={sidePanelStyles.operatorPopup} 
          style={{ 
            position: "absolute", 
            top: "50%", 
            left: "50%", 
            transform: "translate(-50%, -50%)", 
            zIndex: 99
          }}
        >
          {OPERATORS.map((op) => (
            <div
              key={op}
              className={`${sidePanelStyles.operatorItem} ${value === op ? sidePanelStyles.activeOperator : ""}`}
              onClick={() => { 
                onChange(op); 
                setOpen(false); 
              }}
              style={{ padding: "4px" }}
            >
              <OperatorIcon type={op} size={14} />
            </div>
          ))}
        </div>
      )}
      <div
        className={sidePanelStyles.addBtn}
        onClick={() => setOpen((o) => !o)}
        title="Change operator"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
      >
        <OperatorIcon type={value} size={15} />
      </div>
    </div>
  );
};

const Checkbox = ({ label, checked, onChange }) => {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", userSelect: "none" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange && onChange(e.target.checked)}
        style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px" }}
      />
      <span style={{ fontSize: "14px", color: "#33584E", fontWeight: "500" }}>{label}</span>
    </label>
  );
};

const AddItemModal = ({
  groupingData,
  addItemLevel,
  addItemCat,
  addItemHandler,
  addToSubgroup,
  setAddItemModal,
  l1id,
  l1idx,
  l2id,
  l2idx,
  l3idx,
}) => {
  const [selectedItem, setSelectedItem] = useState("line item");
  const [itemName, setItemName] = useState(null);
  const [lineItemNature, setLineItemNature] = useState("credit");

  const [isBold, setIsBold] = useState(false);
  const [isTotal, setIsTotal] = useState(false);
  const [isSubtotal, setIsSubtotal] = useState(false);

  const handleTotalToggle = (val) => {
    setIsTotal(val);
    if (val) {
      setIsBold(true);
      setIsSubtotal(false);
    }
  };

  const handleSubtotalToggle = (val) => {
    setIsSubtotal(val);
    if (val) {
      setIsBold(true);
      setIsTotal(false);
    }
  };

  // ensure every item has operator
  const [formulaItems, setFormulaItems] = useState([
    { element: Date.now(), value: "", operator: "positive" },
  ]);
  const [pendingOperator, setPendingOperator] = useState("positive");

  const operatorOptions = [
    { value: "positive", label: "positive", symbol: "+", Icon: PlusIcon },
    { value: "negative", label: "negative", symbol: "-", Icon: MinusIcon },
    { value: "multiply", label: "multiply", symbol: "×", Icon: MultiplyIcon },
    { value: "divide", label: "divide", symbol: "÷", Icon: DivisionIcon },
  ];

  const ReturnItemsObj = () => {
    if (!itemName || itemName.trim() === "") {
      toast.error("Please enter a name");
      return;
    }
    const returnFormulaItems = formulaItems
      .filter(
        (x) =>
          x.value &&
          (x.value.value || x.value.label || typeof x.value === "string")
      )
      .map((x) => ({
        name:
          typeof x.value === "string"
            ? x.value
            : x.value.value ?? x.value.label ?? "",
        sign: x.operator ?? "positive",
      }));

    const addItemData = {
      level: addItemLevel,
      category: addItemCat,
      lineType: selectedItem,
      name: itemName,
      nature: lineItemNature,
      formula: returnFormulaItems,
      subgroup: [],
      isBold: isBold || isTotal || isSubtotal,
      formulaType: isTotal ? "total" : isSubtotal ? "subtotal" : null,
    };
    if (addItemLevel === 2 || addItemLevel === 3) {
      delete addItemData.category;
    }
    if (addItemLevel === 3) {
      delete addItemData.subgroup;
    }

    if (addItemLevel === 1) {
      addItemHandler(addItemData);
    }
    const l1index = l1id;
    if (addItemLevel === 2) {
      addToSubgroup([l1index], addItemData, addItemCat);
    }
    if (addItemLevel === 3) {
      addToSubgroup([l1index, l2idx], addItemData, addItemCat);
    }
  };

  // const getRootItemsForFormula = () => {
  //   if (!Array.isArray(groupingData)) return [];
  //   if (addItemLevel === 1) return groupingData;
  //   if (addItemLevel === 2) {
  //     if (typeof l1idx === "number" && groupingData[l1idx]) {
  //       const l1 = groupingData[l1idx];
  //       return Array.isArray(l1?.subgroup) ? l1.subgroup : [];
  //     }
  //     return groupingData;
  //   }
  //   if (addItemLevel === 3) {
  //     if (
  //       typeof l1idx === "number" &&
  //       typeof l2idx === "number" &&
  //       groupingData[l1idx]
  //     ) {
  //       const l1 = groupingData[l1idx];
  //       const l2 = Array.isArray(l1?.subgroup) ? l1.subgroup[l2idx] : null;
  //       return Array.isArray(l2?.subgroup) ? l2.subgroup : [];
  //     }
  //     return groupingData;
  //   }
  //   return groupingData;
  // };
  const getRootItemsForFormula = () => {
  if (!Array.isArray(groupingData)) return [];

  // LEVEL 1 dropdown
  if (addItemLevel === 1) {
    return groupingData;
  }

  // LEVEL 2 dropdown
  if (addItemLevel === 2) {
    const selectedL1 = groupingData.find(
      (item) => item._id === l1id
    );

    return selectedL1?.subgroup || [];
  }

  // LEVEL 3 dropdown
  if (addItemLevel === 3) {
    const selectedL1 = groupingData.find(
      (item) => item._id === l1id
    );

    if (!selectedL1) return [];

    const selectedL2 = selectedL1.subgroup?.find(
      (item) => item._id === l2id
    );

    return selectedL2?.subgroup || [];
  }

  return [];
};

  const extractItems = (items = [], levelToMatch) => {
    const result = [];
    const seen = new Set();
    const traverse = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const node of nodes) {
        if (node.level === levelToMatch && node.lineType === "line item") {
          const key = `${node.level}||${node.name}`;
          if (!seen.has(key)) {
            seen.add(key);
            result.push({ label: node.name, value: node.name });
          }
        }
        if (Array.isArray(node.subgroup) && node.subgroup.length) {
          traverse(node.subgroup);
        }
      }
    };
    traverse(items);
    return result;
  };

  const formulaItemList = extractItems(getRootItemsForFormula(), addItemLevel);

  const addFormulaItem = () => {
    setFormulaItems((prev) => [
      ...prev,
      {
        element: Date.now() + Math.floor(Math.random() * 1000),
        value: "",
        operator: pendingOperator,
      },
    ]);
  };

  const updateFormulaItem = (index, value) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value };
      return copy;
    });
  };

  const updateFormulaOperator = (index, operator) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], operator };
      return copy;
    });
  };

  const removeFormulaItem = (index) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      if (copy.length === 0) {
        copy.push({ element: Date.now(), value: "", operator: "positive" });
      }
      return copy;
    });
  };

  const formulaDisplayNode = (() => {
    if (!formulaItems || formulaItems.length === 0) return "";
    const pieces = formulaItems.map((it) => {
      if (!it || !it.value) return "(?)";
      if (typeof it.value === "string") return it.value;
      if (it.value.label) return it.value.label;
      return it.value.value ?? "(?)";
    });
    
    const elements = [];
    elements.push(<React.Fragment key="first">{pieces[0] || ""}</React.Fragment>);
    
    for (let i = 1; i < pieces.length; i++) {
      elements.push(
        <React.Fragment key={`op-${i}`}>
          {" "}
          <span style={{ fontWeight: "bold", color: "#000", display: "inline-flex", alignItems: "center", gap: "2px", verticalAlign: "middle" }}>
            <span>(</span>
            <span style={{ display: "flex", alignItems: "center", transform: "translateY(-1.5px)" }}><OperatorIcon type={formulaItems[i].operator} size={15} /></span>
            <span>)</span>
          </span>{" "}
          {pieces[i]}
        </React.Fragment>
      );
    }
    return elements;
  })();

  return (
    <div
      className={sidePanelStyles.sidePanel}
      style={{
        width: selectedItem === "formula" && formulaItems.length > 1 ? "1050px" : "650px",
        minWidth: selectedItem === "formula" && formulaItems.length > 1 ? "1050px" : "650px",
        maxWidth: selectedItem === "formula" ? "95vw" : "650px",
      }}
    >
      <div className={sidePanelStyles.panelHeader}>
        <span style={{ fontSize: "17px" }}>Add Level {addItemLevel} {selectedItem === "formula" ? "Formula" : "Line Item"}</span>
        <span 
          className={sidePanelStyles.close} 
          onClick={() => setAddItemModal(false)}
        >
          ✕
        </span>
      </div>

      <div className={sidePanelStyles.panelBody} style={{ padding: "20px" }}>
        <div className={libcss.customRadioSection} style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap", gap: "24px" }}>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="responsibilities"
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            style={{ display: "flex", flexDirection: "row", gap: "16px", margin: 0 }}
          >
            <FormControlLabel
              value="line item"
              className={libcss.customRadioLabel}
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label="Line Item"
            />
            <FormControlLabel
              className={libcss.customRadioLabel}
              value="formula"
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label="Formula"
            />
          </RadioGroup>

          {(selectedItem === "formula" || selectedItem === "line item") && (
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0, marginLeft: "auto" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: isTotal || isSubtotal ? "not-allowed" : "pointer", fontSize: "14px", fontWeight: "500", color: "#33584E" }}>
                <input
                  type="checkbox"
                  checked={isBold || isTotal || isSubtotal}
                  onChange={(e) => {
                    if (isTotal || isSubtotal) return;
                    setIsBold(e.target.checked);
                  }}
                  onClick={(e) => {
                    if (isTotal || isSubtotal) {
                      e.preventDefault();
                    }
                  }}
                  style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px", cursor: isTotal || isSubtotal ? "not-allowed" : "pointer" }}
                />
                Bold Format
              </label>
              <Checkbox
                label="Total"
                checked={isTotal}
                onChange={handleTotalToggle}
              />
              <Checkbox
                label="Subtotal"
                checked={isSubtotal}
                onChange={handleSubtotalToggle}
              />
            </div>
          )}
        </div>

        {selectedItem === "line item" && (
          <>
            <InputGroup
              class_="mb-2"
              label="Line Item Name"
              inputs={[
                <>
                  <input
                    name="line item name"
                    className={adminPortalCss.lineItemNameInput}
                    value={itemName ?? ""}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="Enter line item name"
                  />
                </>,
              ]}
            />
            {addItemLevel === 1 && (
              <div className={commoncss.dFlexJustifysb}>
                <span className={`${commoncss.label3} ${commoncss.fs13}`}>
                  Nature of Line Item
                </span>
                <div className={libcss.customRadioSection}>
                  <RadioGroup
                    row
                    name="lineItemNature"
                    value={lineItemNature}
                    onChange={(e) => setLineItemNature(e.target.value)}
                  >
                    <FormControlLabel
                      value="credit"
                      className={libcss.customRadioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label="Credit"
                    />
                    <FormControlLabel
                      value="debit"
                      className={libcss.customRadioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label="Debit"
                    />
                  </RadioGroup>
                </div>
              </div>
            )}
          </>
        )}

        {selectedItem === "formula" && (
          <div>
            <div style={{ width: "100%" }}>
              <InputGroup
                class_="mb-2"
                label="Formula Name"
                inputs={[
                  <>
                    <input
                      name="Formula name"
                      value={itemName ?? ""}
                      onChange={(e) => setItemName(e.target.value)}
                      placeholder="Enter formula name"
                    />
                  </>,
                ]}
              />

              <div className={`${libcss.displayFormula} mt-4`} style={{ whiteSpace: "normal", wordBreak: "break-word" , margin: "30px 0"}}>
                <span className={`${commoncss.label5} ${commoncss.fw500}`} style={{ display: "block" }}>
                  Formula ={" "}
                  {formulaDisplayNode || "(no formula selected)"}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "20px 12px",
                flexWrap: "wrap",
                marginTop: "16px"
              }}
            >
              {formulaItems.map((fitem, index) => (
                <React.Fragment key={fitem.element ?? index}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}
                  >
                    <div className={libcss.formulaCard} style={{ margin: 0, width: "185px", height: "115px", padding: "10px", position: "relative" }}>
                      <CloseIcon
                        className={libcss.formulaCardCloseIcon}
                        style={{ position: "absolute", top: "8px", right: "8px", cursor: "pointer", width: "12px", height: "12px" }}
                        onClick={() => removeFormulaItem(index)}
                      />
                      <span
                        className={`${commoncss.label3} ${commoncss.fw600}`}
                      >
                        LEVEL {addItemLevel}
                      </span>

                      <div style={{ width: "100%" }}>
                        <Dropdown
                          class2_={commoncss.bgWhite}
                          options={formulaItemList}
                          name="formula"
                          selected={fitem.value}
                          onSelect={(value) => updateFormulaItem(index, value)}
                          placeHolder="Select item"
                        />
                      </div>
                    </div>

                    {index < formulaItems.length - 1 && (
                      <div style={{ marginTop: "10px" }}>
                        <OperatorBtn
                          value={formulaItems[index + 1].operator}
                          onChange={(v) => updateFormulaOperator(index + 1, v)}
                        />
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {formulaItems.length > 0 && (
                   <div style={{ marginTop: "10px" }}>
                    <OperatorBtn
                      value={pendingOperator}
                      onChange={(v) => setPendingOperator(v)}
                    />
                  </div>
                )}

                <div
                  className={sidePanelStyles.addElementBox1}
                  onClick={addFormulaItem}
                  style={{ width: "175px",minWidth: "200px", height: "115px", marginTop: "0px", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 500, color: "#B3C0BD" }}><AddIcon width={16} height={16} style={{ marginTop: "-2px" }} /> Add Element</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={sidePanelStyles.footer}>
        <button 
          className={sidePanelStyles.cancelBtn} 
          onClick={() => setAddItemModal(false)}
        >
          Cancel
        </button>
        <button 
          className={sidePanelStyles.applyBtn} 
          onClick={ReturnItemsObj}
        >
          Create
        </button>
      </div>
    </div>
  );
};

export default AddItemModal;
